import React, { useState } from "react";
import {
    Button,
    ButtonGroup,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    Typography
} from "@mui/material";
import { Add, ArrowDropDown, GroupWork } from "@mui/icons-material";
import { BLOCK_TYPES, BlockType } from "./blockTypes";

// Minimal shape of a predefined template group as offered by the Add menu.
// Hosts pass their fuller row type (a structural superset) via the generic.
export type TemplateGroupSummary = {
    id: string,
    name?: string | null,
    description?: string | null,
    sections?: { id: string }[] | null,
};

// The document-level "Add" menu (design §3): "Add section" always; blank block
// types only while the document has no sections; predefined groups (host
// supplied) with a section count per entry.
function ContractAddMenu<T extends TemplateGroupSummary = TemplateGroupSummary>({ anchorEl, open, hasSections, templates, templatesLoading, onAddSection, onAddBlockType, onApplyTemplate, onClose }: {
    anchorEl: HTMLElement | null,
    open: boolean,
    hasSections: boolean,
    templates?: T[],
    templatesLoading?: boolean,
    onAddSection: () => void,
    onAddBlockType: (type: BlockType) => void,
    onApplyTemplate?: (template: T) => void,
    onClose: () => void,
}): React.ReactElement {
    return (
        <Menu open={open} anchorEl={anchorEl} onClose={onClose}>
            <ListItem disableGutters>
                <ListItemButton onClick={() => { onAddSection(); onClose(); }}>
                    <ListItemIcon sx={{ minWidth: 34 }}><Add fontSize="small" /></ListItemIcon>
                    <ListItemText>Add section</ListItemText>
                </ListItemButton>
            </ListItem>
            {!hasSections && BLOCK_TYPES.map(bt => (
                <ListItem key={bt.type} disableGutters>
                    <ListItemButton onClick={() => { onAddBlockType(bt.type); onClose(); }}>
                        <ListItemIcon sx={{ minWidth: 34 }}>{bt.icon}</ListItemIcon>
                        <ListItemText>{bt.label}</ListItemText>
                    </ListItemButton>
                </ListItem>
            ))}
            {templates !== undefined && (
                <>
                    <Divider />
                    <Typography variant="subtitle2" color="textSecondary" sx={{ px: 2, pt: 1, pb: 0.5 }}>Predefined groups</Typography>
                    {templatesLoading ? (
                        <List disablePadding>
                            <ListItem disableGutters>
                                <ListItemText><CircularProgress size={20} /></ListItemText>
                            </ListItem>
                        </List>
                    ) : templates.length === 0 ? (
                        <List disablePadding>
                            <ListItem disableGutters>
                                <ListItemText primary={<Typography variant="body2" color="textSecondary">No predefined groups available</Typography>} />
                            </ListItem>
                        </List>
                    ) : (
                        <List dense disablePadding>
                            {templates.map(t => (
                                <ListItem key={t.id} disableGutters>
                                    <ListItemButton disabled={!onApplyTemplate} onClick={() => { onApplyTemplate?.(t); onClose(); }}>
                                        <ListItemIcon sx={{ minWidth: 34 }}><GroupWork fontSize="small" /></ListItemIcon>
                                        <ListItemText primary={t.name || 'Untitled group'} secondary={(t.sections ?? []).length + ' section' + ((t.sections ?? []).length === 1 ? '' : 's')} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </>
            )}
        </Menu>
    );
}

// The document-level "Add" split control (design §2): primary click adds an
// empty section at the end; the arrow opens the Add menu. Shared by the
// campaign Contract pane and the Contract Templates form.
function AddControl<T extends TemplateGroupSummary = TemplateGroupSummary>({ hasSections, templates, templatesLoading, onAddSection, onAddBlockType, onApplyTemplate }: {
    hasSections: boolean,
    templates?: T[],
    templatesLoading?: boolean,
    onAddSection: () => void,
    onAddBlockType: (type: BlockType) => void,
    onApplyTemplate?: (template: T) => void,
}): React.ReactElement {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    return (
        <>
            <ButtonGroup size="small">
                <Button startIcon={<Add />} onClick={onAddSection}>Add</Button>
                <IconButton size="small" edge="end" aria-label="More add options" onClick={(evt) => setMenuAnchor(evt.currentTarget)}><ArrowDropDown /></IconButton>
            </ButtonGroup>
            <ContractAddMenu
                anchorEl={menuAnchor}
                open={!!menuAnchor}
                hasSections={hasSections}
                templates={templates}
                templatesLoading={templatesLoading}
                onAddSection={onAddSection}
                onAddBlockType={onAddBlockType}
                onApplyTemplate={onApplyTemplate}
                onClose={() => setMenuAnchor(null)}
            />
        </>
    );
}

export default ContractAddMenu;
export { AddControl };
